// Файлы: загрузка и подписанные ссылки на готовое. Экраны 07 и 08.
//
// Ссылка спрашивается по требованию, а не приезжает вместе с артефактом, и
// это не лишний запрос. Подпись у неё со сроком: положи её в артефакт — и
// результат, открытый из истории через неделю, вёл бы по протухшей ссылке,
// а нагрузка артефакта хранила бы мусор, который нельзя обновить.

import { ApiError, failureBody, OfflineError, type Client } from './client.ts'

/** Что сервер принимает и до какого размера. Экран показывает это до выбора. */
export interface UploadLimits {
  warning: string
  types: { kind: string; label: string; extensions: string[]; maxBytes: number }[]
}

export interface Uploaded {
  fileId: string
}

/**
 * Текст на ответ без нашего текста. SMY-328
 *
 * Такой ответ приходит не от api, а с дороги: api рвёт соединение, когда тело не
 * дошло за срок чтения, и Caddy отвечает на обрыв 502 с пустым телом. «Ошибка
 * 502» под полем человеку ничего не говорит, а причина у всех таких ответов для
 * него одна — файл не дошёл.
 */
export const UPLOAD_BROKEN = 'Файл не дошёл до сервера: связь оборвалась или он шёл слишком долго. Попробуй ещё раз'

export const createFiles = (client: Client) => ({
  limits: () => client.request<UploadLimits>('/v1/files/limits'),

  /**
   * Загрузить файл. Идёт мимо `client.request`: тот шлёт JSON, а тут
   * multipart, и границу формы браузер обязан проставить сам — задать
   * `content-type` руками значит отправить форму без границы, и сервер её не
   * разберёт.
   *
   * Проверять размер и тип ДО загрузки — дело вызывающего (`checkDeclared` в
   * ядре): сказать «файл слишком большой» после отправки двадцати мегабайт по
   * мобильной сети поздно.
   */
  async upload(file: File): Promise<Uploaded> {
    const form = new FormData()
    form.append('file', file, file.name)

    const token = client.tokens.get()
    let res: Response
    try {
      res = await fetch(`${client.baseUrl}/v1/files`, {
        method: 'POST',
        ...(token != null ? { headers: { authorization: `Bearer ${token}` } } : {}),
        body: form,
      })
    } catch (cause) {
      throw new OfflineError(cause)
    }

    const text = await res.text()
    // Отказ — общим разбором клиента (SMY-353): не-JSON страница прокси и
    // английский отказ Fastify тоже идут человеку текстом UPLOAD_BROKEN.
    if (!res.ok) throw new ApiError(res.status, failureBody(text, UPLOAD_BROKEN))
    let body: Record<string, unknown> = {}
    if (text !== '') {
      try {
        body = JSON.parse(text) as Record<string, unknown>
      } catch {
        body = { error: text.slice(0, 200) }
      }
    }
    return body as unknown as Uploaded
  },

  /**
   * Ссылка на скачивание. `null` — файла нет, он чужой или ещё не проверен
   * антивирусом: сервер отвечает на все три случая одинаково, и различать их
   * на клиенте нечем и незачем.
   */
  async url(fileId: string): Promise<string | null> {
    try {
      const res = await client.request<{ url: string }>(`/v1/files/${encodeURIComponent(fileId)}/url`)
      return res.url
    } catch {
      return null
    }
  },
})

export type FilesApi = ReturnType<typeof createFiles>
